import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EVENT_DETAIL_QUERY_KEY } from "../../event-detail/hooks/useEventDetail";
import {
	type AdminEventOccasion,
	type CreateOccasionPayload,
	createOccasion,
	deleteOccasion,
	getOccasionDetail,
	getOccasionTemplates,
	type OccasionDetailResponse,
	updateOccasion,
} from "../api/occasionApi";

export const useCreateOccasion = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (payload: CreateOccasionPayload) => createOccasion(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin-event-occasions"] });
			queryClient.invalidateQueries({ queryKey: ["occasion-templates"] });
		},
	});
};

export const useUpdateOccasion = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			occasionId,
			payload,
		}: {
			occasionId: number;
			payload: Partial<CreateOccasionPayload>;
		}) => updateOccasion(occasionId, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin-event-occasions"] });
			queryClient.invalidateQueries({ queryKey: ["occasion-templates"] });
			queryClient.invalidateQueries({ queryKey: [EVENT_DETAIL_QUERY_KEY] });
		},
	});
};

export const useDeleteOccasion = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (occasionId: number) => deleteOccasion(occasionId),
		onSuccess: (_data, occasionId) => {
			queryClient.invalidateQueries({ queryKey: ["admin-event-occasions"] });
			queryClient.removeQueries({
				queryKey: [EVENT_DETAIL_QUERY_KEY, occasionId],
			});
		},
	});
};

export const useOccasionTemplates = (enabled = true) => {
	return useQuery<AdminEventOccasion[]>({
		queryKey: ["occasion-templates"],
		queryFn: getOccasionTemplates,
		staleTime: 60_000,
		enabled,
	});
};

export const useOccasionDetail = (occasionId: number | undefined) => {
	return useQuery<OccasionDetailResponse>({
		queryKey: ["occasion-detail", occasionId],
		queryFn: () => getOccasionDetail(occasionId || 0),
		enabled: !!occasionId,
		staleTime: 30_000,
	});
};
